/* eslint-disable max-len */
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import _ from 'lodash';
import {
  Button, Item, ProgressCircle, TabList, TabPanels, Tabs, Text,
} from '@adobe/react-spectrum';
import DetailNotFound from '../../components/Details/DetailNotFound';
import { formatNumber, scrollToSection } from '../../util/util';
import CompanyInfoTab from './CompanyInfoTab';
import ServicesIndustriesTab from './ServicesIndustriesTab';
import ResourcesTab from './ResourcesTab';
import ExpertiseTab from './ExpertiseTab';
import LocationsTab from './LocationsTab';
import SocialMediaIcons from '../../components/Filters/SocialMediaIcons';
import OpenURLButton from '../../components/Details/OpenURLButton';
import ViewPhoneNumberButton from '../../components/Details/ViewPhoneNumberButton';
import ContactUs from '../List/Action/ContactUs';

export const detailPageLoadingSpinner = (partnerDetail, LoadingAnimation, router) => {
  const { error, companyInfo } = partnerDetail;

  if (error === 404) {
    return <DetailNotFound />;
  }

  return (
    <AnimatePresence>
      {(!_.has(companyInfo, 'listingId') || decodeURI(companyInfo.listingUrl) !== router.params.id) && (
      <motion.div
        initial={LoadingAnimation.initial}
        animate={LoadingAnimation.animate}
        exit={LoadingAnimation.exit}
        transition={LoadingAnimation.transition}
        className="detail-loading-overlay"
      >
        <ProgressCircle UNSAFE_className="detail-loading-spinner" aria-label="detail-loading-spinner" zIndex={3} isIndeterminate />
      </motion.div>
      )}
    </AnimatePresence>
  );
};

export const renderAvailableTabs = (
  visibleTabs,
  isMobile,
  partnerDetail,
  programType,
  handleTabChange,
  defaultTab,
) => {
  const tabs = [];

  if (visibleTabs.includes('companyInfo')) {
    tabs.push({
      key: 'companyInfo',
      name: 'Overview',
      children:
  <>
    {!isMobile
      ? (
        <div className="jumpSection">
          <Text UNSAFE_style={{ fontWeight: 'bolder' }}>Jump to:</Text>
          {partnerDetail.companyInfo.shortDescription && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('company-info-tab', isMobile)}>About us</Button>}
          {partnerDetail.industries?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('industries-section', isMobile)}>{`Industries served (${formatNumber(partnerDetail.industries.length)})`}</Button>}
          {partnerDetail.services?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('services-section', isMobile)}>{`Services offered (${formatNumber(partnerDetail.services.length)})`}</Button>}
        </div>
      )
      : (
        <>
          <Text UNSAFE_style={{ fontWeight: 'bolder' }}>Jump to:</Text>
          <div className="jumpSection">
            {partnerDetail.companyInfo.shortDescription && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('company-info-tab', isMobile)}>About us</Button>}
            {partnerDetail.industries?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('industries-section', isMobile)}>{`Industries served (${formatNumber(partnerDetail.industries.length)})`}</Button>}
            {partnerDetail.services?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('services-section', isMobile)}>{`Services offered (${formatNumber(partnerDetail.services.length)})`}</Button>}
          </div>
        </>
      )}
    <CompanyInfoTab
      shortDescription={partnerDetail.companyInfo.shortDescription}
      globalParentId={partnerDetail.companyInfo.globalParentId}
      longDescription={partnerDetail.companyInfo.longDescription}
      companyName={partnerDetail.companyInfo.name}
      globalParentName={partnerDetail.companyInfo.globalParentName}
      programType={programType}
    />
    <ServicesIndustriesTab services={partnerDetail.services} industries={partnerDetail.industries} />
  </>,
    });
  }

  if (visibleTabs.includes('expertise')) {
    tabs.push({
      key: 'expertise',
      name: 'Expertise',
      children:
  <>
    {!isMobile
      ? (
        <div className="jumpSection">
          <Text UNSAFE_style={{ fontWeight: 'bolder' }}>Jump to:</Text>
          {partnerDetail.companyInfo.specializations > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('specialization-section', isMobile)}>{`Specializations (${formatNumber(partnerDetail.companyInfo.specializations)})`}</Button>}
          {partnerDetail.companyInfo.certifications > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('certification-section', isMobile)}>{`Certifications (${formatNumber(partnerDetail.companyInfo.certifications)})`}</Button>}
          {partnerDetail.totalCustomerDeploymentCount > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('deployment-section', isMobile)}>{`Customer Deployments (${formatNumber(partnerDetail.totalCustomerDeploymentCount)})`}</Button>}
          {partnerDetail.accreditedSolutions.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('solution-section', isMobile)}>{`Partner Solutions (${formatNumber(partnerDetail.accreditedSolutions.length)})`}</Button>}
        </div>
      ) : (
        <>
          <Text UNSAFE_style={{ fontWeight: 'bolder' }}>Jump to:</Text>
          <div className="jumpSection">
            {partnerDetail.companyInfo.specializations > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('specialization-section', isMobile)}>{`Specializations (${formatNumber(partnerDetail.companyInfo.specializations)})`}</Button>}
            {partnerDetail.companyInfo.certifications > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('certification-section', isMobile)}>{`Certifications (${formatNumber(partnerDetail.companyInfo.certifications)})`}</Button>}
            {partnerDetail.totalCustomerDeploymentCount > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('deployment-section', isMobile)}>{`Customer Deployments (${formatNumber(partnerDetail.totalCustomerDeploymentCount)})`}</Button>}
            {partnerDetail.accreditedSolutions.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('solution-section', isMobile)}>{`Partner Solutions (${formatNumber(partnerDetail.accreditedSolutions.length)})`}</Button>}
          </div>
        </>
      ) }
    <ExpertiseTab
      solutionExpertise={partnerDetail.expertise}
      customerDeployments={partnerDetail.deployments}
      specializations={partnerDetail.specializations}
      companyInfo={partnerDetail.companyInfo}
      totalCustomerDeploymentCount={partnerDetail.totalCustomerDeploymentCount}
      accreditedSolutions={partnerDetail.accreditedSolutions}
      isMobile={isMobile}
      programType={programType}
    />
  </>,
    });
  }

  if (visibleTabs.includes('locations')) {
    tabs.push({ key: 'locations', name: 'Locations', children: <LocationsTab locations={partnerDetail.addresses} /> });
  }

  if (visibleTabs.includes('resources')) {
    tabs.push({
      key: 'resources',
      name: 'Additional partner materials',
      children:
  <>
    {!isMobile
      ? (
        <div className="jumpSection">
          <Text UNSAFE_style={{ fontWeight: 'bolder' }}>Jump to:</Text>
          {partnerDetail.resources?.Video?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('video-section', isMobile)}>{`Videos (${formatNumber(partnerDetail.resources?.Video?.length)})`}</Button>}
          {partnerDetail.resources['Success story']?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('case-study-section', isMobile)}>{`Success Studies (${formatNumber(partnerDetail.resources['Success story']?.length)})`}</Button>}
          {partnerDetail.resources['White paper']?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('white-paper-section', isMobile)}>{`White Papers (${formatNumber(partnerDetail.resources['White paper']?.length)})`}</Button>}
          {partnerDetail.resources['Data sheet']?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('data-sheets-section', isMobile)}>{`Data Sheets (${formatNumber(partnerDetail.resources['Data sheet']?.length)})`}</Button>}
        </div>
      )
      : (
        <>
          <Text UNSAFE_style={{ fontWeight: 'bolder' }}>Jump to:</Text>
          <div className="jumpSection">
            {partnerDetail.resources?.Video?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('video-section', isMobile)}>{`Videos (${formatNumber(partnerDetail.resources?.Video?.length)})`}</Button>}
            {partnerDetail.resources['Success story']?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('case-study-section', isMobile)}>{`Success Studies (${formatNumber(partnerDetail.resources['Success story']?.length)})`}</Button>}
            {partnerDetail.resources['White paper']?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('white-paper-section', isMobile)}>{`White Papers (${formatNumber(partnerDetail.resources['White paper']?.length)})`}</Button>}
            {partnerDetail.resources['Data sheet']?.length > 0 && <Button UNSAFE_className="jumpButtons" onPress={() => scrollToSection('data-sheets-section', isMobile)}>{`Data Sheets (${formatNumber(partnerDetail.resources['Data sheet']?.length)})`}</Button>}
          </div>
        </>
      )}
    <ResourcesTab resources={partnerDetail.resources} />
  </>
      ,
    });
  }

  if (tabs.length === 0) {
    return <Text>No tabs available</Text>;
  }

  return (
    <Tabs isQuiet onSelectionChange={handleTabChange} defaultSelectedKey={defaultTab} orientation="horizontal" items={tabs}>
      <TabList UNSAFE_className="tabsList">
        {(item) => (
          <Item key={item.key} justifyContent="center">
            {item.name}
          </Item>
        )}
      </TabList>
      <TabPanels UNSAFE_className="tabPannels">
        {(item) => (
          <Item>
            {item.children}
          </Item>
        )}
      </TabPanels>
    </Tabs>
  );
};

export const mobileInfo = (
  partnerDetail,
  handleContactUsModal,
  companyDetails,
  isOpen,
  handleContactUsClear,
  programPath,
) => (
  <div className="mobile-info-container">
    <div className="mobile-info">
      <div>
        <div className="partner-name">
          {partnerDetail?.companyInfo?.name}
        </div>
        <div className="partner-location">
          {partnerDetail?.companyInfo?.location}
        </div>
      </div>

      {!partnerDetail.preview ? (
        <Button
          UNSAFE_style={{
            background: ' #000000E6 0% 0% no-repeat padding-box',
            color: '#FFFFFF',
            cursor: 'pointer',
            border: '2px solid #000000E6',
            borderRadius: '20px',
          }}
          onPress={() => handleContactUsModal(partnerDetail.companyInfo.listingId, partnerDetail.companyInfo.name)}
        >
          <Text UNSAFE_style={{ fontWeight: 'bold' }}>Contact Us</Text>
        </Button>
      ) : <div />}
      <SocialMediaIcons apiResponse={partnerDetail.companyInfo} detailsPage />
      <div className="button-group">
        <OpenURLButton
          variant="primary"
          translatePath="partner_finder.detail.visit_website"
          websiteUrl={partnerDetail?.companyInfo?.website}
          target="_blank"
        />
        <ViewPhoneNumberButton phoneNumber={partnerDetail?.companyInfo?.phone} />
      </div>
      <ContactUs
        actionType="contactUs"
        initialData={{}}
        companyDetails={companyDetails}
        isOpen={isOpen}
        handleContactUsClear={handleContactUsClear}
        programPath={programPath}
      />
    </div>
  </div>
);
